import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Switch, Space, message, ColorPicker, InputNumber, Upload, Tooltip, Image, Popover } from 'antd';
import { PlusOutlined, EditOutlined, UploadOutlined, ReloadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { PostTrofeo, TestTrofeoCondition, ExecuteTrofeoJob } from '../../../dao/jutge/GetJavamonConfig';
import { getBase64, javamonImage } from './javamonUtils';

interface TabTrofeosProps {
    isAdmin: boolean;
    loading: boolean;
    trofeos: any[];
    fetchTrofeos: () => Promise<void>;
}

export const TabTrofeos: React.FC<TabTrofeosProps> = ({
    isAdmin,
    loading,
    trofeos,
    fetchTrofeos
}) => {
    const [trofeoModal, setTrofeoModal] = useState(false);
    const [editingItem, setEditingItem] = useState<any>(null);
    const [fileList, setFileList] = useState<any[]>([]);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [form] = Form.useForm();
    const [executing, setExecuting] = useState(false);

    const handleSaveTrofeo = async (values: any) => {
        try {
            const formData = new FormData();
            if (fileList.length > 0 && fileList[0].originFileObj) {
                formData.append('file', fileList[0].originFileObj);
            }

            const body: any = {
                ...values,
                id: editingItem?.id || 0,
                active: values.active ? 1 : 0,
                color: typeof values.color === 'string' ? values.color : values.color?.toHexString() || '#FFD700'
            };

            formData.append('json', JSON.stringify(body));

            const res = await PostTrofeo(formData);
            if (!res) throw new Error('Error');
            message.success("Trofeo guardado");
            setTrofeoModal(false);
            setFileList([]);
            setPreviewImage(null);
            fetchTrofeos();
        } catch (e) {
            message.error("Error al guardar trofeo");
        }
    };

    const handleTestTrofeo = async () => {
        const reqType = form.getFieldValue("req_type");
        const reqValue = form.getFieldValue("req_value");
        if (!reqValue) {
            message.warning("La condición está vacía");
            return;
        }

        const res = await TestTrofeoCondition(reqType, reqValue);
        if (res && res.success) {
            if (reqType === 'QUERY') {
                Modal.info({
                    title: 'Resultado de la Query',
                    width: 600,
                    content: (
                        <div>
                            <p>La query devuelve <b>{res.data.length}</b> resultados.</p>
                            <div style={{ maxHeight: 300, overflow: 'auto', background: '#f5f5f5', padding: 10 }}>
                                <pre>{JSON.stringify(res.data, null, 2)}</pre>
                            </div>
                        </div>
                    )
                });
            } else {
                message.success(res.msg || "Procedure ejecutado");
            }
        } else {
            Modal.error({
                title: 'Error en la Query',
                content: res?.error || "Error desconocido"
            });
        }
    };

    const handleExecuteTrofeo = async (id: number) => {
        try {
            setExecuting(true);
            const res = await ExecuteTrofeoJob(id);
            if (res && res.success) {
                message.success(`Trofeo ejecutado correctamente. Asignado a ${res.awarded} usuario(s) nuevos.`);
                fetchTrofeos();
            } else {
                message.error(res?.error || "Error al ejecutar el trofeo");
            }
        } catch (e) {
            message.error("Error al contactar con el servidor");
        } finally {
            setExecuting(false);
        }
    };

    const columnsTrofeo = [
        {
            title: 'Imagen',
            dataIndex: 'imagen',
            key: 'imagen',
            render: (_: any, record: any) => <Image src={javamonImage(record)} alt="img" width={50} />
        },
        { title: 'Nombre', dataIndex: 'nom', key: 'nom' },
        { title: 'Descripción', dataIndex: 'descr', key: 'descr' },
        { title: 'Tipo', dataIndex: 'req_type', key: 'req_type', render: (val: string) => <Tag color="geekblue">{val}</Tag> },
        {
            title: 'Valor/Condición',
            dataIndex: 'req_value',
            key: 'req_value',
            render: (val: string) => (
                <Popover content={<div style={{ maxWidth: 500, maxHeight: 300, overflow: 'auto' }}><pre>{val}</pre></div>} title="Condición">
                    <div style={{ maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', color: '#1890ff' }}>
                        {val}
                    </div>
                </Popover>
            )
        },
        { title: 'Máx Ganadores', dataIndex: 'max_winners', key: 'max_winners', render: (val: number) => val === -1 ? 'Infinito' : val },
        {
            title: 'Ganadores',
            dataIndex: 'ganadores',
            key: 'ganadores',
            render: (val: string) => {
                if (!val) return <span style={{ color: '#ccc' }}>Nadie aún</span>;
                const list = val.split(', ');
                return (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 250 }}>
                        {list.map((name: string, i: number) => <Tag key={i} color="blue">{name}</Tag>)}
                    </div>
                );
            }
        },
        { title: 'Activo', dataIndex: 'active', key: 'active', render: (val: number) => <Tag color={val ? 'green' : 'red'}>{val ? 'SÍ' : 'NO'}</Tag> },
        ...(isAdmin ? [{
            title: 'Acciones',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    <Tooltip title="Ejecutar ahora (como el Job de las 7am)">
                        <Button type="primary" shape="circle" icon={<PlayCircleOutlined />} onClick={() => handleExecuteTrofeo(record.id)} loading={executing} />
                    </Tooltip>
                    <Tooltip title="Editar">
                        <Button icon={<EditOutlined />} onClick={() => {
                            setEditingItem(record);
                            form.setFieldsValue({
                                ...record,
                                active: !!record.active
                            });
                            setTrofeoModal(true);
                            setFileList([]);
                            setPreviewImage(null);
                        }} />
                    </Tooltip>
                </Space>
            )
        }] : [])
    ];

    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                {isAdmin && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingItem(null);
                            form.resetFields();
                            setTrofeoModal(true);
                            setFileList([]);
                            setPreviewImage(null);
                        }}
                    >
                        Crear Trofeo
                    </Button>
                )}
                <Button icon={<ReloadOutlined />} onClick={fetchTrofeos} loading={loading}>
                    Refrescar
                </Button>
            </Space>

            <Table dataSource={trofeos} columns={columnsTrofeo} loading={loading} rowKey="id" />

            {/* Modal Trofeo */}
            <Modal
                title={editingItem ? "Editar Trofeo" : "Crear Trofeo"}
                open={trofeoModal}
                onCancel={() => setTrofeoModal(false)}
                onOk={() => form.submit()}
                destroyOnClose
                width={700}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSaveTrofeo}
                    initialValues={{ active: true, req_type: 'QUERY', max_winners: -1, color: '#FFD700' }}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item name="nom" label="Nombre del Trofeo" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="color" label="Color Destacado">
                            <ColorPicker showText />
                        </Form.Item>
                    </div>

                    <Form.Item name="descr" label="Descripción">
                        <Input.TextArea rows={2} />
                    </Form.Item>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item name="req_type" label="Tipo de Condición">
                            <Select>
                                <Select.Option value="QUERY">QUERY (Devuelve IDs de Usuarios)</Select.Option>
                                <Select.Option value="PROCEDURE">PROCEDURE</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="max_winners" label="Máx. Ganadores (-1 para Infinito)">
                            <InputNumber min={-1} style={{ width: '100%' }} />
                        </Form.Item>
                    </div>

                    <Form.Item label="Condición (Query SQL o Nombre de Procedimiento)" required>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                            <Form.Item name="req_value" noStyle rules={[{ required: true }]}>
                                <Input.TextArea rows={4} placeholder="SELECT id FROM doctorat.usuari WHERE id IN (...)" style={{ fontFamily: 'monospace' }} />
                            </Form.Item>
                            <Button type="dashed" onClick={handleTestTrofeo}>Probar Condición</Button>
                        </div>
                    </Form.Item>

                    <Form.Item label="Subir imagen/icono">
                        <Upload
                            beforeUpload={() => false}
                            fileList={fileList}
                            onChange={async ({ fileList }) => {
                                setFileList(fileList);
                                if (fileList.length > 0 && fileList[0].originFileObj) {
                                    const b64 = await getBase64(fileList[0].originFileObj);
                                    setPreviewImage(b64);
                                } else {
                                    setPreviewImage(null);
                                }
                            }}
                            maxCount={1}
                        >
                            <Button icon={<UploadOutlined />}>Seleccionar archivo</Button>
                        </Upload>
                        {(previewImage || editingItem) && (
                            <div style={{ marginTop: 10 }}>
                                <Image
                                    src={previewImage || javamonImage(editingItem)}
                                    alt="preview"
                                    width={100}
                                    style={{ borderRadius: 8, border: '1px solid #d9d9d9', padding: 4 }}
                                />
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item name="active" label="Trofeo Activo" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
